import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CACHE_KEY_GETALLUSERGLOBALFILTER } from "../../../utils/constants";
import { UserGlobalFilter } from "../../../models/UserModel/UserGlobalFilter";
import userGlobalFilterService from "../../../services/userService/userGlobalFilterService";
import ToastMessage from "../../../common/ToastMessage";

interface useUpdateUserGlobalFilterContext {
  previousGlobalFilters: UserGlobalFilter[];
}

const useUpdateUserGlobalFilter = (userId: string) => {
  const queryClient = useQueryClient();

  return useMutation<
    UserGlobalFilter,
    Error,
    UserGlobalFilter,
    useUpdateUserGlobalFilterContext
  >({
    mutationFn: async (globalFilter: UserGlobalFilter) => {
      return userGlobalFilterService.Update(globalFilter);
    },

    onMutate: async (updatedGlobalFilter: UserGlobalFilter) => {
      await queryClient.cancelQueries({
        queryKey: [CACHE_KEY_GETALLUSERGLOBALFILTER, userId],
      });

      const previousGlobalFilters =
        queryClient.getQueryData<UserGlobalFilter[]>([
          CACHE_KEY_GETALLUSERGLOBALFILTER,
          userId,
        ]) || [];

      queryClient.setQueryData<UserGlobalFilter[]>(
        [CACHE_KEY_GETALLUSERGLOBALFILTER, userId],
        (globalFilters = []) =>
          globalFilters.map((globalFilter) =>
            globalFilter.id === updatedGlobalFilter.id
              ? updatedGlobalFilter
              : globalFilter
          )
      );

      return { previousGlobalFilters };
    },

    onSuccess: (savedGlobalFilter) => {
      ToastMessage.Success();
      queryClient.setQueryData<UserGlobalFilter[]>(
        [CACHE_KEY_GETALLUSERGLOBALFILTER, userId],
        (globalFilters) =>
          globalFilters?.map((globalFilter) =>
            globalFilter.id === savedGlobalFilter.id
              ? savedGlobalFilter
              : globalFilter
          )
      );
    },

    onError: (error, updatedGlobalFilter, context) => {
      if (!context) return;
      ToastMessage.Failed(error.message);
      queryClient.setQueryData<UserGlobalFilter[]>(
        [CACHE_KEY_GETALLUSERGLOBALFILTER, userId],
        context.previousGlobalFilters
      );
    },
  });
};

export default useUpdateUserGlobalFilter;
